/* ---- <product-selector> ---- */
(() => {
  'use strict';

  const json = (el) => { try { return JSON.parse(el?.textContent || '[]'); } catch (e) { return []; } };
  const money = (cents, box) => {
    const t = window.theme;
    if (!t?.formatMoney) return (cents / 100).toFixed(2);
    return t.formatMoney(cents, box?.hasAttribute('data-currency-code') === true);
  };

  const rowVideos = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((entry) => {
        const row = entry.target;
        row.onScreen = entry.isIntersecting;
        if (entry.isIntersecting) row.playVideo(); else row.pauseVideo();
      }), { threshold: 0.25 })
    : { observe: (row) => { row.onScreen = true; } };

  const setupQueue = [];
  let setupRunning = false;
  const nextTask = () => (window.scheduler?.yield ? window.scheduler.yield() : new Promise((r) => setTimeout(r, 0)));
  const runSetup = async () => {
    setupRunning = true;
    let start = performance.now();
    while (setupQueue.length) {
      const el = setupQueue.shift();
      if (!el.isConnected) { el.bound = false; continue; }
      try { el.setup(); } catch (e) { console.warn('[product-selector]', e); }
      if (performance.now() - start > 8) { await nextTask(); start = performance.now(); }
    }
    setupRunning = false;
  };

  class ProductSelector extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      setupQueue.push(this);
      if (!setupRunning) runSetup();
    }

    get variants() {
      if (!this._variants) this._variants = json(this.querySelector('[data-variants]'));
      return this._variants;
    }

    setup() {
      this.buttons = [...this.querySelectorAll('[data-option-index]')];
      this.selection = [];
      this.buttons.forEach((b) => { if (this.isPicked(b)) this.selection[+b.dataset.optionIndex] = b.dataset.value; });
      if (!this.selection.length && this.variants.length === 1) this.selection = (this.variants[0].options || []).slice();

      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-row-thumb]');
        if (thumb) {
          e.preventDefault();
          e.stopPropagation();
          this.showImage(thumb.dataset.rowThumb);
          if (thumb.dataset.rowVideo) this.showVideo(thumb.dataset.rowVideo);
          else this.hideVideo();
          this.querySelectorAll('[data-row-thumb]').forEach((t) => t.setAttribute('aria-pressed', String(t === thumb)));
          return;
        }
        const btn = e.target.closest('[data-option-index]');
        if (!btn || btn.tagName === 'INPUT' || !this.contains(btn)) return;
        e.preventDefault();
        e.stopPropagation();
        this.pick(btn);
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[data-option-index]');
        if (input) this.pick(input);
      });

      this.sync(false);

      const autoplay = this.querySelector('[data-row-autoplay]')?.dataset.rowAutoplay;
      if (autoplay) this.showVideo(autoplay);
    }

    /* ---- The row card's video ---- */
    showVideo(id) {
      const main = this.querySelector('.prow__main');
      if (!main) return;
      let wrap = main.querySelector(`.prow__video[data-video-id="${id}"]`);
      if (!wrap) {
        const template = main.querySelector(`template[data-row-video-template="${id}"]`);
        if (!template) return;
        wrap = document.createElement('div');
        wrap.className = 'prow__video';
        wrap.dataset.videoId = id;
        wrap.appendChild(template.content.cloneNode(true));
        const video = wrap.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.setAttribute('muted', '');
        video.addEventListener('playing', () => wrap.classList.add('is-playing'));
        main.appendChild(wrap);
      }
      if (this.activeVideo !== wrap) this.hideVideo();
      wrap.hidden = false;
      this.activeVideo = wrap;
      rowVideos.observe(this);
      if (this.onScreen) this.playVideo();
    }

    hideVideo() {
      const wrap = this.activeVideo;
      if (!wrap) return;
      const video = wrap.querySelector('video');
      video?.pause();
      try { video.currentTime = 0; } catch (e) { }
      wrap.classList.remove('is-playing');
      wrap.hidden = true;
      this.activeVideo = null;
    }

    playVideo() {
      const video = this.activeVideo?.querySelector('video');
      if (!video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      Promise.resolve(video.play()).catch(() => {});
    }

    pauseVideo() { this.activeVideo?.querySelector('video')?.pause(); }

    isPicked(el) {
      return el.tagName === 'INPUT' ? el.checked : el.getAttribute('aria-pressed') === 'true';
    }

    pick(el) {
      const index = +el.dataset.optionIndex;
      this.selection[index] = el.dataset.value;
      this.buttons
        .filter((b) => +b.dataset.optionIndex === index)
        .forEach((b) => {
          if (b.tagName === 'INPUT') b.checked = b === el;
          else b.setAttribute('aria-pressed', String(b === el));
        });
      this.sync(true);
    }

    match(selection = this.selection) {
      return this.variants.find((v) => (v.options || []).every((o, i) => !selection[i] || o === selection[i]));
    }

    get complete() {
      return this.variants.length > 0 && this.selection.filter(Boolean).length >= (this.variants[0].options || []).length;
    }

    sync(paint) {
      this.buttons.forEach((b) => {
        const index = +b.dataset.optionIndex;
        const reachable = this.variants.some((v) => v.available
          && v.options[index] === b.dataset.value
          && v.options.every((o, i) => i === index || !this.selection[i] || o === this.selection[i]));
        b.dataset.unavailable = String(!reachable);
      });

      const previous = this.variant;
      const variant = this.match();
      this.variant = variant;

      this.updateAdd(variant);
      this.updateStock(variant);
      if (!paint || !variant) return;

      this.updateLabels();
      this.updatePrice(variant);
      this.updateMedia(variant);
      this.updateBadges(variant);
      this.updateLinks(variant);

      if (variant.id !== previous?.id) {
        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, selection: this.selection } }));
      }
    }

    updateAdd(variant) {
      const ok = !!variant && variant.available;
      this.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = variant?.id || '';
        input.disabled = !ok;
      });
      const t = window.theme?.strings || {};
      this.querySelectorAll('[data-add]').forEach((btn) => {
        btn.disabled = !ok;
        const preorderIds = (btn.closest('[data-preorder-ids]')?.dataset.preorderIds || '').split(',');
        const isPreorder = !!variant && preorderIds.includes(String(variant.id));
        const addText = isPreorder ? (btn.dataset.preorderText || btn.dataset.addText || t.addToCart) : (btn.dataset.addText || t.addToCart);
        const label = btn.querySelector('[data-add-label]');
        if (label) label.textContent = ok ? (addText || label.textContent) : (variant ? (t.soldOut || label.textContent) : (t.unavailable || label.textContent));
        const aria = ok ? addText : variant ? t.soldOut : t.unavailable;
        if (aria && btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', aria);
      });
      this.classList.toggle('is-sold-out', !!variant && !variant.available);
    }

    updateLabels() {
      this.querySelectorAll('[data-selected-option]').forEach((out) => {
        const value = this.selection[+out.dataset.selectedOption];
        if (value) out.textContent = value;
      });
    }

    updatePrice(variant) {
      // Until every option is picked, the price can still vary: show "From" the lowest price among the variants left.
      const left = this.complete ? [variant] : this.variants.filter((v) => (v.options || []).every((o, i) => !this.selection[i] || o === this.selection[i]));
      const low = Math.min(...left.map((v) => v.price));
      const from = window.theme?.strings?.fromPrice;
      const varies = !!from && left.some((v) => v.price !== low);
      const shown = varies ? left.find((v) => v.price === low) : variant;
      this.querySelectorAll('[data-price] .price').forEach((box) => {
        const regular = box.querySelector('.price__item--regular');
        const onSale = shown.compare > shown.price;
        if (regular) regular.textContent = varies ? from.replace('[price]', money(low, box)) : money(variant.price, box);
        let compare = box.querySelector('.price__item--compare');
        if (onSale && !compare) {
          compare = document.createElement('s');
          compare.className = 'price__item price__item--compare';
          if (regular) regular.after(compare); else box.appendChild(compare);
        }
        if (compare) {
          compare.textContent = onSale ? money(shown.compare, box) : '';
          compare.hidden = !onSale;
        }
        box.classList.toggle('price--on-sale', onSale);
        box.classList.toggle('price--sold-out', !variant.available);
      });
    }

    // Pictures are listed once in [data-variant-images] as { mediaId: [src, srcset] }; variants point to them by "img".
    imageFor(variant) {
      if (!variant?.img) return null;
      if (!this._images) {
        try { this._images = JSON.parse(this.querySelector('[data-variant-images]')?.textContent || '{}'); } catch (e) { this._images = {}; }
      }
      const entry = this._images[variant.img];
      return entry ? { src: entry[0], srcset: entry[1] } : null;
    }

    updateMedia(variant) {
      const image = this.imageFor(variant);
      if (!image) return;
      this.hideVideo();
      this.showImage(image.src, image.srcset);
      const hover = this.querySelector('.product-card__hover');
      if (!hover) return;
      if (hover.querySelector('card-video')) {
        hover.hidden = true;
        return;
      }
      this.showImage(image.src, image.srcset, hover.querySelector('img'));
    }

    showImage(src, srcset, img = this.querySelector('[data-primary-media] img')) {
      if (!img || !src || img.dataset.src === src) return;
      img.dataset.src = src;
      img.classList.add('is-swapping');
      const settle = () => img.classList.remove('is-swapping');
      img.addEventListener('load', settle, { once: true });
      img.addEventListener('error', settle, { once: true });
      img.srcset = srcset || '';
      img.src = src;
      if (img.complete) settle();
    }

    updateStock(variant) {
      const stack = this.querySelector('[data-badges]');
      if (!stack) return;

      const soldOut = stack.querySelector('[data-badge-sold-out]');
      if (soldOut) soldOut.hidden = !(variant && !variant.available);

      const holder = stack.querySelector('[data-badge-variant]');
      if (!holder) return;
      const badge = variant ? variant.badge : null;
      holder.hidden = !badge;
      if (!badge) return;
      const text = holder.querySelector('[data-badge-variant-text]') || holder;
      text.textContent = badge.text;
      holder.style.setProperty('--badge-bg', badge.bg || holder.dataset.badgeBg || '');
      holder.style.setProperty('--badge-text', badge.text_color || holder.dataset.badgeTextColor || '');
    }

    updateBadges(variant) {
      const stack = this.querySelector('[data-badges]');
      const badge = stack?.querySelector('[data-badge-sale]');
      if (!badge) return;
      const onSale = variant.available && variant.compare > variant.price;
      badge.hidden = !onSale;
      if (!onSale) return;
      const text = badge.querySelector('[data-badge-text]') || badge;
      if (stack.dataset.saleFormat === 'percent') {
        const percent = Math.round(((variant.compare - variant.price) / variant.compare) * 100);
        text.textContent = (stack.dataset.salePercent || '[percent]%').replace('[percent]', percent);
      } else if (stack.dataset.saleFormat === 'amount') {
        text.textContent = (stack.dataset.saleAmount || '[amount]').replace('[amount]', money(variant.compare - variant.price));
      }
    }

    updateLinks(variant) {
      const url = this.dataset.url;
      if (!url) return;
      const href = `${url}${url.includes('?') ? '&' : '?'}variant=${variant.id}`;
      this.querySelectorAll('[data-card-link]').forEach((a) => { a.href = href; });
    }
  }

  if (!customElements.get('product-selector')) customElements.define('product-selector', ProductSelector);

  /* ---- <quick-add> ---- */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.run(); });
    }
    get selector() { return this.closest('product-selector'); }
    get target() {
      const selector = this.selector;
      if (!selector) return null;
      const chosen = selector.variant;
      if (chosen?.available && selector.complete) return chosen;
      const selection = selector.selection || [];
      return (selector.variants || []).find((v) => v.available
        && (v.options || []).every((o, i) => !selection[i] || o === selection[i])) || null;
    }
    async run() {
      if (this.button.getAttribute('aria-busy') === 'true') return;
      const variant = this.target;
      if (!variant) {
        const url = this.selector?.dataset.url || this.closest('[data-card-link], a[href*="/products/"]')?.href;
        if (url) location.href = url;
        return;
      }
      this.button.setAttribute('aria-busy', 'true');
      try {
        const qty = parseInt(this.selector?.querySelector('[data-quantity]')?.value, 10);
        const line = { id: variant.id, quantity: qty > 0 ? qty : 1 };
        const preorderIds = (this.dataset.preorderIds || '').split(',');
        if (this.dataset.preorderName && preorderIds.includes(String(variant.id))) {
          line.properties = { [this.dataset.preorderName]: this.dataset.preorderValue || '' };
        }
        const added = await window.theme.cart.add([line], { opener: this.button });
        if (!added) return;
        const delay = window.theme.addedDelay ?? 700;
        this.button.classList.add('is-added');
        setTimeout(() => this.button.classList.remove('is-added'), delay + 700);
        if (window.theme.cartType !== 'drawer') setTimeout(() => { location.href = `${window.Shopify?.routes?.root || '/'}cart`; }, delay);
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('quick-add')) customElements.define('quick-add', QuickAdd);

  /* ---- <card-video> ---- */
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CAN_HOVER = window.matchMedia('(hover: hover)');

  class CardVideo extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      if (!CAN_HOVER.matches) return;

      const card = this.closest('.card') || this.parentElement;
      if (!card) return;
      card.addEventListener('pointerenter', () => this.start());
      card.addEventListener('pointerleave', () => this.stop());
      card.addEventListener('focusin', () => this.start());
      card.addEventListener('focusout', (e) => { if (!card.contains(e.relatedTarget)) this.stop(); });
    }

    get video() {
      if (this.player) return this.player;
      const template = this.querySelector('template');
      if (!template) return null;
      this.appendChild(template.content.cloneNode(true));
      template.remove();
      this.player = this.querySelector('video');
      if (this.player) {
        this.player.muted = true;
        this.player.addEventListener('playing', () => this.classList.add('is-playing'));
      }
      return this.player;
    }

    start() {
      if (REDUCED_MOTION.matches) return;
      const video = this.video;
      if (!video) return;
      Promise.resolve(video.play()).catch(() => {});
    }

    stop() {
      const video = this.player;
      if (!video) return;
      video.pause();
      try { video.currentTime = 0; } catch (e) { }
      this.classList.remove('is-playing');
    }
  }
  if (!customElements.get('card-video')) customElements.define('card-video', CardVideo);

  /* ---- <ft-countdown> ---- */
  const clocks = new Set();
  let timer = null;

  const pad = (n) => String(n).padStart(2, '0');

  const tick = () => {
    if (!clocks.size) { clearInterval(timer); timer = null; return; }
    const now = Date.now();
    clocks.forEach((el) => el.paint(now));
  };

  // Each clock paints its time at once; the every-second ticking waits for the page's load event, so it doesn't
  // add a re-render a second while the page is still loading.
  const loaded = document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
  const startTimer = () => loaded.then(() => { if (clocks.size && !timer) timer = setInterval(tick, 1000); });

  const watcher = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((entry) => {
        const el = entry.target;
        if (entry.isIntersecting) { clocks.add(el); el.paint(Date.now()); } else clocks.delete(el);
        if (clocks.size && !timer) startTimer();
      }), { rootMargin: '200px' })
    : null;

  class FtCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end) * 1000;
      this.out = this.querySelector('[data-clock]');
      if (!this.end || !this.out) return;
      this.parts = [...this.out.querySelectorAll('[data-unit]')];
      if (watcher) watcher.observe(this); else { clocks.add(this); startTimer(); }
      this.paint(Date.now());
    }
    disconnectedCallback() {
      clocks.delete(this);
      watcher?.unobserve(this);
    }
    paint(now) {
      let left = Math.floor((this.end - now) / 1000);
      if (left <= 0) {
        clocks.delete(this);
        this.remove();
        return;
      }
      const days = Math.floor(left / 86400); left -= days * 86400;
      const hours = Math.floor(left / 3600); left -= hours * 3600;
      const minutes = Math.floor(left / 60);
      const seconds = left - minutes * 60;
      const values = { days, hours, minutes, seconds };
      this.parts.forEach((part) => {
        const value = pad(values[part.dataset.unit] ?? 0);
        if (part.textContent !== value) part.textContent = value;
      });
    }
  }
  if (!customElements.get('ft-countdown')) customElements.define('ft-countdown', FtCountdown);
})();
